import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useExportCompetitions } from "~pages/Competitions/CompetitionListPage/hooks/useExportCompetitions.ts";
import { useImportCompetitions } from "~pages/Competitions/CompetitionListPage/hooks/useImportCompetitions.ts";
import { useCreateCompetition } from "~pages/Competitions/hooks/useCreateCompetition.ts";
import useDeleteCompetition from "~pages/Competitions/hooks/useDeleteCompetition.ts";
import { useUpdateCompetition } from "~pages/Competitions/hooks/useUpdateCompetition.ts";
import type { CreateCompetition, UpdateCompetition } from "~pages/Competitions/types/upsertCompetition.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";

export const useCompetitionService = () => {
  const { mutateAsync: createCompetitionFn, isPending: isCreatePending } = useCreateCompetition();
  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteCompetition();
  const { mutateAsync: updateCompetitionFn, isPending: isUpdatePending } = useUpdateCompetition();
  const { mutateAsync: importCompetitionsFn, isPending: isImportPending } = useImportCompetitions();
  const { mutateAsync: exportCompetitionsFn, isPending: isExportPending } = useExportCompetitions();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove, executeOrThrow } = useApiOperations(ApiOperationItems.COMPETITION);

  const createCompetition = async (payload: CreateCompetition) => {
    const createdCompetition = await create(() => createCompetitionFn(payload), {
      successMessageId: "competition.create.success",
    });

    if (createdCompetition) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/competitions`, { state: { search: payload.nameLocal } });
    }

    return createdCompetition;
  };

  const updateCompetition = (competitionId: string, payload: Omit<UpdateCompetition, "competitionId">) =>
    update(() => updateCompetitionFn({ ...payload, competitionId }), { successMessageId: "competition.update.success" });

  const deleteCompetition = async (competitionId: string) =>
    Boolean(await remove(() => deleteFn(competitionId), { successMessageId: "competition.delete.success" }));

  const importCompetitions = async (fileName: string) =>
    executeOrThrow(() => importCompetitionsFn(fileName), {
      successMessageId: "competitions.import.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  const exportCompetitions = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    executeOrThrow(() => exportCompetitionsFn(fieldNames), {
      successMessageId: "competitions.export.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  return {
    createCompetition,
    isCreatePending,
    updateCompetition,
    isUpdatePending,
    deleteCompetition,
    isDeletePending,
    importCompetitions,
    isImportPending,
    exportCompetitions,
    isExportPending,
  };
};
